import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

// Online Status Types
export interface UserStatus {
  userId: string;
  isOnline: boolean;
  lastSeen: Date;
  status: 'online' | 'away' | 'busy' | 'offline';
}

// Online Status Functions
export const updateUserStatus = async (
  userId: string, 
  isOnline: boolean, 
  status: 'online' | 'away' | 'busy' | 'offline' = 'online'
): Promise<void> => {
  await setDoc(doc(db, 'userStatus', userId), {
    userId,
    isOnline,
    status,
    lastSeen: serverTimestamp()
  });
};

export const subscribeToUserStatus = (
  userId: string,
  callback: (status: UserStatus | null) => void
) => {
  return onSnapshot(doc(db, 'userStatus', userId), (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      callback({
        userId: data.userId,
        isOnline: data.isOnline,
        lastSeen: data.lastSeen?.toDate() || new Date(),
        status: data.status
      });
    } else {
      callback(null);
    }
  });
};

export const subscribeToMultipleUserStatuses = (
  userIds: string[],
  callback: (statuses: { [userId: string]: UserStatus }) => void
) => {
  const statuses: { [userId: string]: UserStatus } = {};
  
  // Subscribe to each user's status
  const unsubscribes = userIds.map(userId => {
    return onSnapshot(doc(db, 'userStatus', userId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        statuses[userId] = {
          userId: data.userId,
          isOnline: data.isOnline,
          lastSeen: data.lastSeen?.toDate() || new Date(),
          status: data.status
        };
      } else {
        statuses[userId] = {
          userId,
          isOnline: false,
          lastSeen: new Date(),
          status: 'offline'
        };
      }
      callback({ ...statuses });
    });
  });

  return () => {
    unsubscribes.forEach(unsubscribe => unsubscribe());
  };
};
